import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { nextReduxCookieMiddleware } from 'next-redux-cookie-wrapper';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { authReducer, authSlice } from '@/store/authSlice';
import { AUTH, RESPONSEMESSAGE } from '@/utils/constants';

import { cookieStorage } from './cookieStorage';
import {
  responseMessageReducer,
  responseMessageSlice,
} from './responseMessageSlice';
import { settingReducer, settingSlice } from './tzosettingSlice';

const authPersistConfig = {
  key: AUTH,
  keyPrefix: '',
  storage: typeof window === 'undefined' ? storage : cookieStorage,
  whitelist: [
    'Mode',
    'authState',
    'emailAddress',
    'loggedInName',
    'token',
    'googleAuthId',
    'appleAuthId',
    'appleAccesToken',
    'facebookAuthId',
    'isCoBrowse',
    'isCoEdit',
    'RefreshToken',
    'isTaxProfessional',
    'locale',
    'RefreshTokenId',
    'isDirty',
    'mainUserName',
    'userAccountCount',
    'isTwoStepVerification',
    'memberRole',
    'isWorkFlowSettingEnabled',
    'isEmailVerified',
    'roleId',
  ],
};

const persistedAuthReducer = persistReducer(authPersistConfig, authReducer);

const responseMessagePersistConfig = {
  key: RESPONSEMESSAGE,
  keyPrefix: '',
  storage: typeof window === 'undefined' ? storage : cookieStorage,
  whitelist: ['messages'],
};

const persistedResponseMessageReducer = persistReducer(
  responseMessagePersistConfig,
  responseMessageReducer
);

const settingPersistConfig = {
  key: 'tzo_settings',
  keyPrefix: '',
  storage: typeof window === 'undefined' ? storage : cookieStorage,
  whitelist: [
    'IsCustomizeSettingEnabled',
    'IsCompanyLogoAttached',
    'CompanyLogoType',
    'CompanyLogoSrc',
    'CompanyLogoFilename',
    'BrandColorCodeType',
    'BrandColorCode',
    'SubDomainName',
    'SiteUrl',
    'SiteName',
    'LoginPageText',
  ],
};

const persistedSettingReducer = persistReducer(
  settingPersistConfig,
  settingReducer
);

const rootReducer = combineReducers({
  tzo_auth: persistedAuthReducer,
  tzo_responseMessage: persistedResponseMessageReducer,
  tzo_settings: persistedSettingReducer,
});

export const makeStore = () => {
  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).prepend(
        nextReduxCookieMiddleware({
          domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
          subtrees: [
            `${authSlice.name}`,
            `${responseMessageSlice.name}`,
            `${settingSlice.name}`,
          ],
          secure: true,
          sameSite: 'lax',
        })
      ),
  });
};

export type AppStore = ReturnType<typeof makeStore>;
export type RootState = ReturnType<AppStore['getState']>;
export type AppDispatch = AppStore['dispatch'];

export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();
export const useAppStore = useStore.withTypes<AppStore>();
